import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coins } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface CreditBadgeProps {
    balance: number | null;
    loading?: boolean;
    compact?: boolean;
}

/**
 * Generation credit balance chip.
 *
 * Deliberately identical in treatment to `LimitBadge` — the two sit next to
 * each other in studio headers, and a vendor reads them as one row of numbers.
 */
export const CreditBadge: React.FC<CreditBadgeProps> = ({ balance, loading, compact }) => {
    const { t } = useTranslation();
    const display = loading ? '…' : balance !== null ? balance.toLocaleString() : '—';

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    className={cn(
                        'inline-flex cursor-default items-center gap-1.5 rounded-lg border border-input font-medium text-foreground select-none',
                        compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
                    )}
                >
                    <span className="flex items-center opacity-60">
                        <Coins className="size-3.5" />
                    </span>
                    <span>{display}</span>
                    <span className="font-normal text-muted-foreground">credits</span>
                </span>
            </TooltipTrigger>
            <TooltipContent>{t('CONTENT_STUDIO.CREDITS_REMAINING_TOOLTIP')}</TooltipContent>
        </Tooltip>
    );
};
